<template>
  <v-flex class="designer">
    <v-container>
      <h1 class="mb-8 mt-2 d-flex align-center">
        <v-icon class="mr-3" large>mdi-pencil</v-icon>
        <span>Experiment Designer</span>
      </h1>

      <!-- accordion list -->
      <v-expansion-panels multiple>
        <v-expansion-panel v-for="(kind, i) in design" :key="i">
          <v-expansion-panel-header>
            <span
              class="body-1 font-weight-bold"
              style="flex:unset; width:6rem;"
            >
              {{ kind.head }}
            </span>
            <v-btn
              text
              color="success"
              style="order:3; flex:unset;"
              @click.stop="openAddDialog(i)"
            >
              <v-icon color="success">mdi-plus-circle</v-icon>
              <span class="ml-2">Add New...</span>
            </v-btn>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row v-if="kind.items">
              <v-col>
                <v-data-table
                  :headers="headers"
                  :items="kind.items"
                  :mobile-breakpoint="MOBILE_BREAK_POINT"
                  style="box-shadow:unset !important"
                >
                  <template v-slot:item.Title="{item}">
                    {{ item.Title }}
                  </template>
                  <template v-slot:item.Color="{item}">
                    <v-chip
                      :color="'#' + item.Color"
                      :dark="!isTextColorDark('#' + item.Color)"
                      style="width: 5rem;"
                    >
                      #{{ item.Color }}
                    </v-chip>
                  </template>
                  <template v-slot:item.ItemId="{item}">
                    <span class="grey--text">{{ item.ItemId }}</span>
                  </template>
                  <template v-slot:item.Action="{item}">
                    <v-btn
                      icon
                      class="mx-1"
                      @click="openEditDialog(i, item)"
                      :loading="item.EditLoading"
                    >
                      <v-icon>mdi-square-edit-outline</v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      class="mx-1"
                      @click="openDeleteDialog(item)"
                      :loading="item.DeleteLoading"
                    >
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- add dialog -->
      <v-dialog
        v-show="addDialog.isShow"
        v-model="addDialog.isShow"
        persistent
        max-width="600px"
        v-scroll-lock="!addDialog.isShow"
      >
        <v-card>
          <v-card-title>
            <v-col class="headline font-weight-bold">Add a new label ...</v-col>
            <v-col class="text-right body-1">
              <v-chip
                :color="addDialog.color.value"
                :dark="!isTextColorDark(addDialog.color.value)"
              >
                {{ addDialog.name.value || 'labelName' }}
              </v-chip>
            </v-col>
          </v-card-title>
          <v-list-item class="px-6">
            <v-list-item-content class="mt-3 px-3">
              <v-form
                ref="form_add"
                v-model="addDialog.valid"
                lazy-validation
                @submit.prevent
              >
                <!-- Name -->
                <v-row dense>
                  <v-col cols="4" class="pt-4">Name</v-col>
                  <v-col cols="8">
                    <v-text-field
                      v-model="addDialog.name.value"
                      outlined
                      required
                      dense
                      name="name"
                      :rules="addDialog.name.rules"
                      :placeholder="design[addDialog.kind].head + ' name'"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <!-- Color -->
                <v-row dense>
                  <v-col cols="4" class="pt-4">Color</v-col>
                  <v-col cols="8">
                    <v-color-picker
                      v-model="addDialog.color.value"
                      flat
                      width="348"
                    ></v-color-picker>
                  </v-col>
                </v-row>
                <!-- Notes -->
                <v-row dense class="mt-7">
                  <v-col cols="4" class="pt-4">Notes</v-col>
                  <v-col cols="8">
                    <v-textarea
                      v-model="addDialog.notes.value"
                      outlined
                      required
                      name="notes"
                      :rules="addDialog.notes.rules"
                      placeholder="notes"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="text-right">
                    <v-btn
                      color="success"
                      @click="addLabel()"
                      :disabled="!addDialog.valid"
                      :loading="addDialog.loading"
                    >
                      ADD
                    </v-btn>
                    <v-btn color="error ml-3" @click="closeAddDialog()">
                      CANCEL
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-list-item-content>
          </v-list-item>
        </v-card>
      </v-dialog>

      <!-- edit dialog -->
      <v-dialog
        v-show="editDialog.isShow"
        v-model="editDialog.isShow"
        persistent
        max-width="600px"
        v-scroll-lock="!editDialog.isShow"
      >
        <v-card v-if="editDialog.item">
          <v-card-title>
            <v-col class="headline font-weight-bold">Edit label ...</v-col>
            <v-col class="text-right body-1">
              <v-chip
                :color="editDialog.color.value"
                :dark="!isTextColorDark(editDialog.color.value)"
              >
                {{ editDialog.name.value || 'labelName' }}
              </v-chip>
            </v-col>
          </v-card-title>
          <v-list-item class="px-6">
            <v-list-item-content class="mt-3 px-3">
              <v-form
                ref="form_edit"
                v-model="editDialog.valid"
                lazy-validation
                @submit.prevent
              >
                <!-- Name -->
                <v-row dense>
                  <v-col cols="4" class="pt-4">Name</v-col>
                  <v-col cols="8">
                    <v-text-field
                      v-model="editDialog.name.value"
                      outlined
                      required
                      dense
                      name="edit_name"
                      :rules="editDialog.name.rules"
                      :placeholder="design[editDialog.kind].head + ' name'"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <!-- Color -->
                <v-row dense>
                  <v-col cols="4" class="pt-4">Color</v-col>
                  <v-col cols="8">
                    <v-color-picker
                      v-model="editDialog.color.value"
                      flat
                      width="348"
                    ></v-color-picker>
                  </v-col>
                </v-row>
                <!-- Notes -->
                <v-row dense class="mt-7">
                  <v-col cols="4" class="pt-4">Notes</v-col>
                  <v-col cols="8">
                    <v-textarea
                      v-model="editDialog.notes.value"
                      outlined
                      required
                      name="edit_notes"
                      :rules="editDialog.notes.rules"
                      placeholder="notes"
                    ></v-textarea>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="text-right">
                    <v-btn
                      color="warning"
                      @click="editLabel()"
                      :disabled="!editDialog.valid"
                      :loading="editDialog.loading"
                    >
                      EDIT
                    </v-btn>
                    <v-btn color="error ml-3" @click="closeEditDialog()">
                      CANCEL
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-list-item-content>
          </v-list-item>
        </v-card>
      </v-dialog>

      <!-- delete dialog -->
      <v-dialog
        v-show="deleteDialog.isShow"
        v-model="deleteDialog.isShow"
        persistent
        max-width="400"
        v-scroll-lock="!deleteDialog.isShow"
      >
        <v-card v-if="deleteDialog.item">
          <v-card-title>
            <v-col class="text-center">
              You are trying to delete this label.
            </v-col>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col class="text-center">
                <v-chip
                  :color="'#' + deleteDialog.item.Color"
                  :dark="!isTextColorDark('#' + deleteDialog.item.Color)"
                >
                  {{ deleteDialog.item.Title }}
                </v-chip>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="success"
              text
              @click="deleteLabel()"
              :loading="deleteDialog.item.DeleteLoading"
            >
              CONTINUE
            </v-btn>
            <v-btn color="error" text @click="closeDeleteDialog()">
              CANCEL
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-flex>
</template>

<script>
export default {
  name: 'brainsdesigner',
  title: 'brains designer',
  data: () => ({
    design: {
      mouseLines: {
        head: 'Mouse lines',
        category: 'strain',
        items: null,
        isAsc: true,
      },
      cellLabels: {
        head: 'Cell labels',
        category: 'marker',
        items: null,
        isAsc: true,
      },
      experiments: {
        head: 'Experiments',
        category: 'experiment',
        items: null,
        isAsc: true,
      },
    },
    headers: [
      {text: 'Name', sortable: true, value: 'Title'},
      {text: 'Color', sortable: true, value: 'Color'},
      {text: 'Notes', sortable: true, value: 'Notes'},
      {text: 'ID', sortable: true, value: 'ItemId'},
      {text: '', value: 'Action', sortable: false, align: 'end'},
    ],
    addDialog: {
      isShow: false,
      kind: 'mouseLines',
      valid: false,
      loading: false,
      name: {
        value: '',
        rules: [
          (v) => !!v || 'Required.',
          (v) => !v || /^[ -~]*$/i.test(v) || 'Invalid characters.',
          (v) => !v || v.length <= 124 || 'Please enter within 124 characters.',
        ],
      },
      color: {
        value: '#ff0000',
        isTextDark: false,
      },
      notes: {
        value: '',
        rules: [
          (v) => !!v || 'Required.',
          (v) => !v || /^[ -~\n\r]*$/i.test(v) || 'Invalid characters.',
          (v) =>
            !v || v.length <= 2048 || 'Please enter within 2048 characters.',
        ],
      },
    },
    editDialog: {
      isShow: false,
      kind: 'mouseLines',
      valid: false,
      loading: false,
      item: null,
      name: {
        value: '',
        rules: [
          (v) => !!v || 'Required.',
          (v) => !v || /^[ -~]*$/i.test(v) || 'Invalid characters.',
          (v) => !v || v.length <= 124 || 'Please enter within 124 characters.',
        ],
      },
      color: {
        value: '#ff0000',
        isTextDark: false,
      },
      notes: {
        value: '',
        rules: [
          (v) => !!v || 'Required.',
          (v) => !v || /^[ -~\n\r]*$/i.test(v) || 'Invalid characters.',
          (v) =>
            !v || v.length <= 2048 || 'Please enter within 2048 characters.',
        ],
      },
    },
    deleteDialog: {
      isShow: false,
      item: null,
    },
  }),
  components: {},
  methods: {
    openAddDialog(kind) {
      this.addDialog.kind = kind
      this.addDialog.isShow = true
    },
    closeAddDialog() {
      this.addDialog.name.value = ''
      this.addDialog.notes.value = ''
      this.$refs.form_add.reset()
      this.addDialog.isShow = false
    },
    openEditDialog(kind, item) {
      this.editDialog.item = item
      this.editDialog.kind = kind
      this.editDialog.name.value = item.Title
      this.editDialog.color.value = `#${item.Color}`
      this.editDialog.notes.value = item.Notes
      this.editDialog.isShow = true
    },
    closeEditDialog() {
      this.$refs.form_edit.reset()
      this.editDialog.loading = false
      this.editDialog.isShow = false
    },
    openDeleteDialog(item) {
      this.deleteDialog.item = item
      this.deleteDialog.isShow = true
    },
    closeDeleteDialog() {
      this.deleteDialog.isShow = false
    },
    async refreshLabel() {
      for (const kind in this.design) {
        if (this.design.hasOwnProperty(kind)) {
          const list = await this.getMyTags(this.design[kind].category)
          if (!list) {
            return
          }
          for (const item of list.Items) {
            item.EditLoading = false
            item.DeleteLoading = false
          }
          this.design[kind].items = list.Items
        }
      }
    },
    async addLabel() {
      if (this.$refs.form_add.validate()) {
        const labelData = {
          Title: this.addDialog.name.value,
          Color: this.addDialog.color.value.slice(1).toLowerCase(),
          Notes: this.addDialog.notes.value,
        }
        this.addDialog.loading = true
        const result = await this.addMyTags(
          this.design[this.addDialog.kind].category,
          labelData
        )
        if (!result) {
          return
        }
        await this.refreshLabel()
        this.addDialog.loading = false
        this.closeAddDialog()
      }
    },
    async editLabel() {
      if (this.$refs.form_edit.validate()) {
        const labelData = {
          Title: this.editDialog.name.value,
          Color: this.editDialog.color.value.slice(1).toLowerCase(),
          Notes: this.editDialog.notes.value,
        }
        this.editDialog.item.EditLoading = true
        this.editDialog.loading = true
        const result = await this.editMyTags(
          this.design[this.editDialog.kind].category,
          labelData,
          this.editDialog.item.ItemId
        )
        if (!result) {
          // Do nothing and continue the process.
        }
        await this.refreshLabel()
        this.editDialog.item.EditLoading = false
        this.closeEditDialog()
      }
    },
    async deleteLabel() {
      this.deleteDialog.item.DeleteLoading = true
      const deletedItem = await this.deleteMyTags(
        this.deleteDialog.item.Category,
        this.deleteDialog.item.ItemId
      )
      if (!deletedItem) this.deleteDialog.item.Loading = false
      this.closeDeleteDialog()
      this.refreshLabel()
    },
  },
  mounted() {
    this.refreshLabel()
  },
}
</script>

<style scoped>
.v-color-picker {
  border: 1px solid rgba(0, 0, 0, 0.24);
}
.designer >>> .v-data-table-header {
  background-color: WhiteSmoke;
}
.designer >>> .v-data-table-header th {
  border-bottom: gray 1px solid !important;
}
</style>
